import { useState, useEffect } from 'react'
import {
  Users,
  Plus,
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  Car,
  Mail,
  Phone,
  Upload,
  Check,
  Eye,
  X,
  KeyRound,
  Shield,
  Calendar,
  Home,
  Copy,
  Lock,
  Loader2,
  Save,
  UserCheck,
  UserX,
  Wifi,
  WifiOff,
  AlertCircle
} from 'lucide-react'
import { getTenants, getTenant, createTenant, generatePin, deleteTenant, deactivateUnifiTenant, adminResetPassword } from '../lib/api'

interface Tenant {
  id: number
  first_name: string
  last_name: string
  email: string | null
  phone: string | null
  room_id: number | null
  status: string
  needs_review: number
  registration_status: 'registered' | 'not_registered'
  has_user_account: boolean
  last_login: string | null
  lease_start: string | null
  lease_end: string | null
  unifi_sync_status: string
  vehicles: { id: number; license_plate: string }[]
  created_at: string
}

interface TenantDetail extends Tenant {
  full_name: string
  is_lease_active: boolean
  needs_review: number
  registration_status: 'registered' | 'not_registered'
  has_user_account: boolean
  last_login: string | null
  unifi_pin_code: string | null
  unifi_subject_id: string | null
  unifi_last_sync: string | null
  unifi_card_token: string | null
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  emergency_contact_relation: string | null
  notes: string | null
  moved_in_at: string | null
  moved_out_at: string | null
  updated_at: string | null
  room_info: { id: number; room_label: string | null; unit_number: string | null; building_name: string | null } | null
}

export default function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [syncFilter, setSyncFilter] = useState<string>('')
  const [registrationFilter, setRegistrationFilter] = useState<string>('')
  const [reviewFilter, setReviewFilter] = useState<string>('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [selectedTenants, setSelectedTenants] = useState<number[]>([])
  const [syncing, setSyncing] = useState(false)
  const [viewingTenant, setViewingTenant] = useState<TenantDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  
  const hasActiveFilters = statusFilter || syncFilter || registrationFilter || reviewFilter

  const handleViewTenant = async (tenantId: number) => {
    setLoadingDetail(true)
    try {
      const data = await getTenant(tenantId) as TenantDetail
      setViewingTenant(data)
    } catch (err) {
      alert('Failed to load tenant details')
    } finally {
      setLoadingDetail(false)
    }
  }

  const fetchTenants = async () => {
    setLoading(true)
    try {
      const data = await getTenants({
        search: search || undefined,
        status: statusFilter || undefined,
        sync_filter: syncFilter || undefined,
        registration_filter: registrationFilter || undefined,
        review_filter: reviewFilter || undefined,
      }) as { tenants: Tenant[] }
      setTenants(data.tenants || [])
    } catch (err) {
      console.error('Failed to load tenants:', err)
    } finally {
      setLoading(false)
    }
  }

  const clearAllFilters = () => {
    setStatusFilter('')
    setSyncFilter('')
    setRegistrationFilter('')
    setReviewFilter('')
  }

  useEffect(() => {
    fetchTenants()
  }, [search, statusFilter, syncFilter, registrationFilter, reviewFilter])

  const handleSelectAll = () => {
    if (selectedTenants.length === tenants.length) {
      setSelectedTenants([])
    } else {
      setSelectedTenants(tenants.map(t => t.id))
    }
  }

  const handleSelectTenant = (id: number) => {
    setSelectedTenants(prev =>
      prev.includes(id)
        ? prev.filter(tid => tid !== id)
        : [...prev, id]
    )
  }

  const handleBulkSync = async () => {
    if (selectedTenants.length === 0) return

    setSyncing(true)
    try {
      const response = await fetch('/api/sync/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(selectedTenants)
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => null)
        throw new Error(errData?.detail || 'Bulk sync failed')
      }

      const result = await response.json()
      
      // Show success notification
      alert(`Sync completed: ${result.synced} succeeded, ${result.failed} failed`)
      
      // Clear selection and refresh
      setSelectedTenants([])
      await fetchTenants()
    } catch (err) {
      console.error('Bulk sync error:', err)
      alert('Failed to sync tenants. Please try again.')
    } finally {
      setSyncing(false)
    }
  }

  const handleDeactivateUnifi = async (tenant: Tenant) => {
    if (!confirm(`Deactivate UniFi access for ${tenant.first_name} ${tenant.last_name}? They will lose door access but remain in the database.`)) return
    try {
      await deactivateUnifiTenant(tenant.id)
      alert(`UniFi access deactivated for ${tenant.first_name} ${tenant.last_name}`)
      await fetchTenants()
    } catch (err) {
      alert('Failed to deactivate UniFi access. ' + (err instanceof Error ? err.message : ''))
    }
  }

  const handleDeleteTenant = async (tenant: Tenant) => {
    const unifiNote = tenant.unifi_sync_status === 'synced'
      ? '\n\nThis will also deactivate their UniFi Access account.'
      : ''
    if (!confirm(`Delete ${tenant.first_name} ${tenant.last_name} from the database?${unifiNote}\n\nThis cannot be undone.`)) return
    try {
      await deleteTenant(tenant.id)
      alert(`${tenant.first_name} ${tenant.last_name} has been deleted.`)
      setSelectedTenants(prev => prev.filter(id => id !== tenant.id))
      await fetchTenants()
    } catch (err) {
      alert('Failed to delete tenant. ' + (err instanceof Error ? err.message : ''))
    }
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      notice: 'bg-orange-100 text-orange-800',
      moved_out: 'bg-gray-100 text-gray-800'
    }
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />
      case 'pending':
        return <Clock className="w-4 h-4" />
      case 'moved_out':
        return <XCircle className="w-4 h-4" />
      default:
        return <Clock className="w-4 h-4" />
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tenants</h1>
          <p className="text-gray-600 mt-1">Manage resident information</p>
        </div>
        <div className="flex gap-3">
          {selectedTenants.length > 0 && (
            <button
              onClick={handleBulkSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : `Sync ${selectedTenants.length} to UniFi`}
            </button>
          )}
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Import Residents
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Tenant
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Filter Chips */}
      <div className="mb-6 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">
            <Filter className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Filters
          </span>

          {/* Tenant Status */}
          <div className="flex gap-1.5">
            {[
              { value: '', label: 'All Status', icon: Users },
              { value: 'active', label: 'Active', icon: CheckCircle },
              { value: 'pending', label: 'Pending', icon: Clock },
              { value: 'notice', label: 'Notice', icon: AlertCircle },
              { value: 'moved_out', label: 'Moved Out', icon: XCircle },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={`status-${value}`}
                onClick={() => setStatusFilter(value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  statusFilter === value
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          <span className="text-gray-300">|</span>

          {/* Sync Status */}
          <div className="flex gap-1.5">
            {[
              { value: 'synced', label: 'Synced', icon: Wifi, activeColor: 'bg-green-600 text-white' },
              { value: 'not_synced', label: 'Not Synced', icon: WifiOff, activeColor: 'bg-orange-500 text-white' },
            ].map(({ value, label, icon: Icon, activeColor }) => (
              <button
                key={`sync-${value}`}
                onClick={() => setSyncFilter(syncFilter === value ? '' : value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  syncFilter === value
                    ? activeColor + ' shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          <span className="text-gray-300">|</span>

          {/* Registration Status */}
          <div className="flex gap-1.5">
            {[
              { value: 'registered', label: 'Registered', icon: UserCheck, activeColor: 'bg-blue-600 text-white' },
              { value: 'not_registered', label: 'Not Registered', icon: UserX, activeColor: 'bg-red-500 text-white' },
            ].map(({ value, label, icon: Icon, activeColor }) => (
              <button
                key={`reg-${value}`}
                onClick={() => setRegistrationFilter(registrationFilter === value ? '' : value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  registrationFilter === value
                    ? activeColor + ' shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          <span className="text-gray-300">|</span>

          {/* Needs Review */}
          <button
            onClick={() => setReviewFilter(reviewFilter === 'needs_review' ? '' : 'needs_review')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              reviewFilter === 'needs_review'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Eye className="w-3.5 h-3.5" />
            Needs Review
          </button>

          {/* Clear all filters */}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-all ml-2"
            >
              <X className="w-3.5 h-3.5" />
              Clear All
            </button>
          )}
        </div>

        {/* Active filter summary */}
        {hasActiveFilters && (
          <div className="text-xs text-gray-500">
            Showing {tenants.length} tenant{tenants.length !== 1 ? 's' : ''} matching filters
          </div>
        )}
      </div>

      {/* Tenants Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No tenants found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedTenants.length === tenants.length && tenants.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registration
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicles
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sync
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedTenants.includes(tenant.id)}
                        onChange={() => handleSelectTenant(tenant.id)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-700 font-semibold">
                            {tenant.first_name[0]}{tenant.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <button
                            onClick={() => handleViewTenant(tenant.id)}
                            className="font-medium text-primary-700 hover:text-primary-800 hover:underline text-left"
                          >
                            {tenant.first_name} {tenant.last_name}
                          </button>
                          {tenant.lease_start && (
                            <p className="text-sm text-gray-500">
                              Since {new Date(tenant.lease_start).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {tenant.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            {tenant.email}
                          </div>
                        )}
                        {tenant.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4" />
                            {tenant.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(tenant.status)}`}>
                          {getStatusIcon(tenant.status)}
                          {tenant.status}
                        </span>
                        {tenant.needs_review === 1 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 animate-pulse">
                            <Eye className="w-3 h-3" />
                            Needs Review
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {tenant.registration_status === 'registered' ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <UserCheck className="w-3 h-3" />
                            Registered
                          </span>
                          {tenant.last_login && (
                            <span className="text-[10px] text-gray-400 pl-1">
                              Last: {new Date(tenant.last_login).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          <UserX className="w-3 h-3" />
                          Not Registered
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {tenant.vehicles.length > 0 ? (
                        <div className="flex items-center gap-1">
                          <Car className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {tenant.vehicles.length} vehicle{tenant.vehicles.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        tenant.unifi_sync_status === 'synced'
                          ? 'bg-green-100 text-green-800'
                          : tenant.unifi_sync_status === 'error'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {tenant.unifi_sync_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewTenant(tenant.id)}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          title="View tenant details"
                        >
                          View
                        </button>
                        {tenant.unifi_sync_status === 'synced' && (
                          <button
                            onClick={() => handleDeactivateUnifi(tenant)}
                            className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                            title="Deactivate UniFi access (keep in database)"
                          >
                            Deactivate
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteTenant(tenant)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                          title="Delete from database (and deactivate UniFi if synced)"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Tenant Modal */}
      {showAddModal && (
        <AddTenantModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false)
            fetchTenants()
          }}
        />
      )}

      {/* Import Residents Modal */}
      {showImportModal && (
        <ImportResidentsModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false)
            fetchTenants()
          }}
        />
      )}

      {/* Tenant Detail Modal */}
      {viewingTenant && (
        <TenantDetailModal
          tenant={viewingTenant}
          onClose={() => setViewingTenant(null)}
        />
      )}

      {/* Loading overlay for detail fetch */}
      {loadingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-6 shadow-xl flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-primary-500 animate-spin" />
            <span className="text-gray-700">Loading tenant details...</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tenant Detail Modal ───────────────────────────────────────────────────

interface TenantDetailModalProps {
  tenant: TenantDetail
  onClose: () => void
}

function TenantDetailModal({ tenant, onClose }: TenantDetailModalProps) {
  const [pinVisible, setPinVisible] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showResetPw, setShowResetPw] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resettingPw, setResettingPw] = useState(false)
  const [pwMessage, setPwMessage] = useState<{ text: string; isError: boolean } | null>(null)

  const handleResetPassword = async () => {
    setPwMessage(null)
    if (!tenant.email) {
      setPwMessage({ text: 'This tenant has no email address on file', isError: true })
      return
    }
    if (newPassword.length < 8) {
      setPwMessage({ text: 'Password must be at least 8 characters', isError: true })
      return
    }
    if (newPassword !== confirmPassword) {
      setPwMessage({ text: 'Passwords do not match', isError: true })
      return
    }
    setResettingPw(true)
    try {
      await adminResetPassword(tenant.email, newPassword)
      setPwMessage({ text: `Password reset successfully for ${tenant.email}`, isError: false })
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setShowResetPw(false), 2000)
    } catch (err) {
      setPwMessage({ text: err instanceof Error ? err.message : 'Failed to reset password', isError: true })
    } finally {
      setResettingPw(false)
    }
  }

  const copyPin = () => {
    if (tenant.unifi_pin_code) {
      navigator.clipboard.writeText(tenant.unifi_pin_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const syncColor = (status: string) => {
    switch (status) {
      case 'synced': return 'bg-green-100 text-green-800 border-green-200'
      case 'error': return 'bg-red-100 text-red-800 border-red-200'
      case 'deactivated': return 'bg-orange-100 text-orange-800 border-orange-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'notice': return 'bg-orange-100 text-orange-800'
      case 'moved_out': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-primary-50 to-white rounded-t-xl">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center ring-4 ring-primary-50">
              <span className="text-primary-700 font-bold text-xl">
                {tenant.first_name[0]}{tenant.last_name[0]}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {tenant.first_name} {tenant.last_name}
              </h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(tenant.status)}`}>
                  {tenant.status}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${syncColor(tenant.unifi_sync_status)}`}>
                  UniFi: {tenant.unifi_sync_status}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  tenant.registration_status === 'registered'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {tenant.registration_status === 'registered'
                    ? <><UserCheck className="w-3 h-3" /> Registered</>
                    : <><UserX className="w-3 h-3" /> Not Registered</>
                  }
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Needs Review Banner */}
          {tenant.needs_review === 1 && (
            <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <Eye className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-purple-900">Self-Registered — Needs Review</p>
                <p className="text-xs text-purple-700 mt-1">
                  This resident signed up through the portal and has not been reviewed yet. Verify their information and assign them to the correct room before approving.
                </p>
              </div>
            </div>
          )}

          {/* PIN Code Section - Highlighted */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <KeyRound className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-900">Door Access PIN Code</p>
                  {tenant.unifi_pin_code ? (
                    <p className="text-2xl font-mono font-bold text-amber-800 tracking-widest mt-0.5">
                      {pinVisible ? tenant.unifi_pin_code : '* * * * *'}
                    </p>
                  ) : (
                    <p className="text-sm text-amber-600 mt-0.5">No PIN assigned</p>
                  )}
                </div>
              </div>
              {tenant.unifi_pin_code && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPinVisible(!pinVisible)}
                    className="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
                  >
                    {pinVisible ? 'Hide' : 'Show'}
                  </button>
                  <button
                    onClick={copyPin}
                    className="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors flex items-center gap-1"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Reset Password Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Lock className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Portal Password</p>
                  <p className="text-xs text-gray-500">Reset this resident's login password</p>
                </div>
              </div>
              {!showResetPw && (
                <button
                  onClick={() => { setShowResetPw(true); setPwMessage(null) }}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  Reset Password
                </button>
              )}
            </div>

            {showResetPw && (
              <div className="mt-4 space-y-3">
                {pwMessage && (
                  <div className={`p-2.5 rounded-lg text-sm ${pwMessage.isError ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                    {pwMessage.text}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                    placeholder="Min 8 characters"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                    placeholder="Re-enter password"
                  />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={handleResetPassword}
                    disabled={resettingPw || !newPassword}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg disabled:opacity-50 transition-colors"
                  >
                    {resettingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {resettingPw ? 'Resetting...' : 'Set New Password'}
                  </button>
                  <button
                    onClick={() => { setShowResetPw(false); setNewPassword(''); setConfirmPassword(''); setPwMessage(null) }}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900 truncate">{tenant.email || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-medium text-gray-900">{tenant.phone || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Unit & Lease Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Unit & Lease</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Home className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Unit</p>
                  <p className="text-sm font-medium text-gray-900">
                    {tenant.room_info
                      ? `${tenant.room_info.building_name || ''} ${tenant.room_info.unit_number || tenant.room_info.room_label || 'Unit ' + tenant.room_info.id}`.trim()
                      : '—'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Lease Period</p>
                  <p className="text-sm font-medium text-gray-900">
                    {tenant.lease_start
                      ? `${new Date(tenant.lease_start).toLocaleDateString()} — ${tenant.lease_end ? new Date(tenant.lease_end).toLocaleDateString() : 'Ongoing'}`
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* UniFi Access Details */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">UniFi Access</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 flex-1 text-sm">
                  <div>
                    <span className="text-gray-500">Sync Status:</span>
                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${syncColor(tenant.unifi_sync_status)}`}>
                      {tenant.unifi_sync_status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Subject ID:</span>
                    <span className="ml-2 text-gray-900 font-mono text-xs">{tenant.unifi_subject_id || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Sync:</span>
                    <span className="ml-2 text-gray-900">
                      {tenant.unifi_last_sync ? new Date(tenant.unifi_last_sync).toLocaleString() : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Card Token:</span>
                    <span className="ml-2 text-gray-900 font-mono text-xs">{tenant.unifi_card_token || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicles */}
          {tenant.vehicles && tenant.vehicles.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Vehicles</h3>
              <div className="space-y-2">
                {tenant.vehicles.map((v) => (
                  <div key={v.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Car className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{v.license_plate}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Emergency Contact */}
          {(tenant.emergency_contact_name || tenant.emergency_contact_phone) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Emergency Contact</h3>
              <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900">{tenant.emergency_contact_name || '—'}</p>
                {tenant.emergency_contact_relation && (
                  <p className="text-xs text-gray-500">{tenant.emergency_contact_relation}</p>
                )}
                {tenant.emergency_contact_phone && (
                  <p className="text-sm text-gray-700 mt-1">{tenant.emergency_contact_phone}</p>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {tenant.notes && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Notes</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{tenant.notes}</p>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex flex-wrap gap-4 text-xs text-gray-400">
              <span>Created: {new Date(tenant.created_at).toLocaleString()}</span>
              {tenant.updated_at && <span>Updated: {new Date(tenant.updated_at).toLocaleString()}</span>}
              {tenant.moved_in_at && <span>Moved in: {new Date(tenant.moved_in_at).toLocaleDateString()}</span>}
              {tenant.moved_out_at && <span>Moved out: {new Date(tenant.moved_out_at).toLocaleDateString()}</span>}
              <span className="text-gray-300">ID: {tenant.id}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Add Tenant Modal ──────────────────────────────────────────────────────

interface AddTenantModalProps {
  onClose: () => void
  onSuccess: () => void
}

function AddTenantModal({ onClose, onSuccess }: AddTenantModalProps) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    pin_code: '',
  })
  const [saving, setSaving] = useState(false)
  const [generatingPin, setGeneratingPin] = useState(false)

  const handleGeneratePin = async () => {
    setGeneratingPin(true)
    try {
      const res = await generatePin()
      setFormData((prev) => ({ ...prev, pin_code: res.pin_code }))
    } catch (err) {
      alert('Failed to generate PIN. Try again.')
    } finally {
      setGeneratingPin(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const { pin_code, ...rest } = formData
      const payload: Record<string, string | undefined> = { ...rest }
      if (pin_code) payload.unifi_pin_code = pin_code
      await createTenant(payload)
      onSuccess()
    } catch (err) {
      alert('Failed to create tenant')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add Tenant</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                required
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PIN Code (door access)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.pin_code}
                placeholder="Optional"
                onChange={(e) => setFormData({ ...formData, pin_code: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleGeneratePin}
                disabled={generatingPin}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {generatingPin ? '...' : 'Generate'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Generates a unique 5-digit PIN not already in use
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Creating...' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

interface ImportResidentsModalProps {
  onClose: () => void
  onSuccess: () => void
}

function ImportResidentsModal({ onClose, onSuccess }: ImportResidentsModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{imported: number, skipped: number, vehicles_created?: number, vehicles_skipped?: number, errors: string[]} | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
      setError('')
      setResult(null)
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file')
      return
    }

    setUploading(true)
    setError('')

    try {
      const formData = new FormData()
      formData.append('file', file)

      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/tenants/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Import failed')
      }

      setResult(data)
      if (data.imported > 0) {
        setTimeout(() => onSuccess(), 2000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Import Residents</h2>
        </div>

        <div className="p-6 space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">File Requirements:</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Excel (.xlsx, .xls) or CSV file</li>
              <li><strong>Resident columns:</strong> first_name, last_name, email (required), phone, pin_code (optional)</li>
              <li><strong>Vehicle columns:</strong> license_plate (or plate/tag), make, model, color, year, plate_state (all optional)</li>
              <li>Email addresses must be unique. PIN codes should be 5 digits.</li>
            </ul>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Excel File
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            {file && (
              <p className="mt-2 text-sm text-gray-600">
                Selected: {file.name}
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {result && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-medium text-green-900 mb-2">Import Complete!</h3>
              <ul className="text-sm text-green-800 space-y-1">
                <li>✓ Imported: {result.imported} residents</li>
                {(result.vehicles_created ?? 0) > 0 && <li>✓ Vehicles: {result.vehicles_created} created</li>}
                {result.skipped > 0 && <li>⊘ Skipped: {result.skipped} duplicate residents</li>}
                {(result.vehicles_skipped ?? 0) > 0 && <li>⊘ Vehicles skipped: {result.vehicles_skipped} (plate already exists)</li>}
                {result.errors.length > 0 && (
                  <li className="text-red-600">✗ Errors: {result.errors.join(', ')}</li>
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? 'Importing...' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}








